import classNames from 'classnames'
import type * as React from 'react'

import type {BuildStatus} from '../../../../types'
import {formatDuration} from '../../../../utils/dateTimeUtils'
import BuildStartTime from '../../BuildStartTime/BuildStartTime'
import Icon from '../../Icon/Icon'
import BuildDurationValue from '../BuildDurationValue/BuildDurationValue'

import styles from './BuildDurationAnchor.module.css'

const innerStyle = (progress: string) => ({
  width: progress,
})

type Props = {
  elapsed?: number
  exact?: boolean
  className?: string
  // seconds
  left?: number
  //seconds
  overtime?: number
  status?: BuildStatus
  progress?: number
  started?: string | null | undefined
  finished?: string | null | undefined
  low?: boolean
  isOvertime?: boolean
  showBuildStartTime?: boolean
}
export default function BuildDurationAnchor(props: Props) {
  function getProgressWidth(): string {
    const progress = isNaN(props.progress ?? 0) ? 0 : Number(props.progress ?? 0)
    return `${progress}%`
  }

  function getProgressTextWidth(): string {
    const hundred = 100
    const progress = isNaN(props.progress ?? 0)
      ? 0
      : Number((hundred * hundred) / (props.progress ?? 0))
    return `${progress}%`
  }

  function getText(): React.ReactNode {
    let result
    const {
      started,
      finished,
      elapsed = 0,
      left,
      overtime,
      exact = false,
      showBuildStartTime = true,
    } = props

    if (started != null && finished != null) {
      if (showBuildStartTime) {
        result = (
          <>
            <BuildStartTime started={started} finished={finished} exact={exact} />
            <span className={styles.durationFinished}>
              <BuildDurationValue
                className={styles.exactDuration}
                started={started}
                finished={finished}
                exact
              />
            </span>
          </>
        )
      } else {
        result = null
      }
    } else if (overtime != null && props.isOvertime) {
      result = (
        <span className={classNames(styles.text, styles.textForOvertime)}>
          {formatDuration(overtime)}
        </span>
      )
    } else if (left == null || left < 0) {
      // no estimation, e.g. first build
      result = <span className={styles.text}>{`${formatDuration(elapsed)} passed`}</span>
    } else {
      result = <span className={styles.text}>{`${formatDuration(left || 0)} left`}</span>
    }

    return result
  }

  function renderContent(): React.ReactNode {
    const {started, finished, status} = props

    if (started == null) {
      return null
    }

    const text = getText()

    if (finished != null) {
      return text
    }

    const isFailure = status !== 'SUCCESS'

    const wrapperClasses = classNames(styles.wrapper, {
      [styles.wrapperSuccess]: !isFailure,
      [styles.wrapperFailure]: isFailure,
      [styles.wrapperOvertime]: props.isOvertime,
    })
    const progressClasses = classNames(styles.progress, {
      [styles.progressFailure]: isFailure,
      [styles.progressOvertime]: props.isOvertime,
    })
    const overtimeIcon = props.isOvertime ? <Icon name="overtime" className={styles.icon} /> : null
    return (
      <div className={wrapperClasses}>
        {text}
        {overtimeIcon}

        <div style={innerStyle(getProgressWidth())} className={progressClasses}>
          <div style={innerStyle(getProgressTextWidth())}>
            {text}
            {overtimeIcon}
          </div>
        </div>
      </div>
    )
  }

  const {className, low, started, finished, showBuildStartTime = true} = props
  const classes = classNames(styles.buildDuration, className, {
    [styles.low]: low,
    [styles.placeholder]: !started,
  })

  if (finished != null && !showBuildStartTime) {
    return null
  }

  return <div className={classes}>{renderContent()}</div>
}
